import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { get } from 'lodash-es';
import { callApi, testPlayerPlayoutAvailable } from 'utils/api';
import { videoModel } from 'common/api/models/video';
import { websiteSelector } from 'common/selectors';

const buildPlayerPlayoutUrl = (playerApiDomain, playerId, videoId) =>
  `https://${playerApiDomain}/play/player/${playerId}/video/${videoId}`;

const getPlayerPlayoutUrl = (website, videoId) => {
  const playerApiDomain = get(website, 'playerConfiguration.playerApiDomain');
  const playerId = get(website, 'playerConfiguration.playerDefinitionId');

  return buildPlayerPlayoutUrl(playerApiDomain, playerId, videoId);
};

const getThumbnailUrl = (video) => {
  if (!video) {
    return null;
  }

  const { playable, thumbnail, displayDate } = video;
  let url = null;

  if (playable && get(thumbnail, 'url')) {
    url = thumbnail.url;
  }

  if (!url && isVideoDisplayDateOverTimeout(displayDate)) {
    url = notFoundUrl;
  }

  return url;
};

const isVideoDisplayDateOverTimeout = (displayDate, timeout = 10) => {
  const createdMinsAgo = displayDate ? parseInt((Date.now() - new Date(displayDate).getTime()) / (1000 * 60), 10) : 0;

  return createdMinsAgo > timeout;
};

const extendTimeout = (time) => {
  return Math.min(time + 1000, 10000);
};

const notFoundUrl = `${window.__MOVINGIMAGE_CT_PUBLIC_URL}/static/img/thumbnail-not-found.png`;

const useThumbnailUrl = (videoFromProps) => {
  const [thumbUrl, setUrl] = useState(() => getThumbnailUrl(videoFromProps));
  const dispatch = useDispatch();
  const website = useSelector(websiteSelector);

  useEffect(() => {
    let isSubscribed = true;
    let trackVideoTimerId;
    let trackVideoTimeout = 1000;
    let playoutTimeoutId;
    let playoutTimeout = 1000;

    if (!videoFromProps) {
      return;
    }

    const waitUntilThumbnailAvailable = (video) => {
      return new Promise((resolve, reject) => {
        const trackVideo = () => {
          if (!isSubscribed) {
            return reject();
          }

          trackVideoTimerId = setTimeout(async () => {
            let resp;
            try {
              resp = videoModel(await callApi(`videos/${video.id}`));
            } catch (e) {
              resp = get(e, 'json');
            }
            const cDate = video.displayDate || get(resp, 'displayDate');

            if (isVideoDisplayDateOverTimeout(cDate)) {
              reject('video ready and over time... give up');
            } else {
              getThumbnailUrl(resp) ? resolve(resp) : retry();
            }
          }, trackVideoTimeout);
        };

        const retry = () => {
          trackVideoTimeout = extendTimeout(trackVideoTimeout);
          trackVideo();
        };

        trackVideo();
      });
    };

    const waitUntilPlayoutReady = (video) => {
      const playoutUrl = getPlayerPlayoutUrl(website, video.id);

      return new Promise((resolve, reject) => {
        const trackPlayoutAvailable = () => {
          if (!isSubscribed) {
            return reject();
          }
          playoutTimeoutId = setTimeout(async () => {
            try {
              await testPlayerPlayoutAvailable(playoutUrl);
              resolve(video);
            } catch (e) {
              if (isVideoDisplayDateOverTimeout(video.displayDate)) {
                reject('playout over time... give up');
              } else {
                playoutTimeout = extendTimeout(playoutTimeout);
                trackPlayoutAvailable();
              }
            }
          }, playoutTimeout);
        };

        trackPlayoutAvailable();
      });
    };

    const setThumbUrl = (newUrl) => {
      if (isSubscribed) {
        setUrl(newUrl);
      }
    };

    const url = getThumbnailUrl(videoFromProps);
    if (videoFromProps.id && !url) {
      waitUntilThumbnailAvailable(videoFromProps)
        .then((video) => waitUntilPlayoutReady(video))
        .then((video) => getThumbnailUrl(video))
        .then((url) => {
          setThumbUrl(url);
        })
        .catch((e) => {
          setThumbUrl(notFoundUrl);
        });
    } else {
      setThumbUrl(url);
    }

    return () => {
      isSubscribed = false;
      clearTimeout(trackVideoTimerId);
      clearTimeout(playoutTimeoutId);
    };
  }, [videoFromProps, website, dispatch]);

  return thumbUrl;
};

export { useThumbnailUrl };
